import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Language, Category, TranslatableText } from '@worldcoo/monolith/core';
import { translatable } from './locale-utils';

export const DEFAULT_DONATION_AMOUNTS = [5, 10, 25, 50];

export type ProjectFormData = {
	id: string;
	internalName: string;
	name: TranslatableText;
	description: TranslatableText;
	justification: TranslatableText;
	objectives: TranslatableText;
	activities: TranslatableText;
	languages: Language[];
	defaultLanguage: Language;
	countryCodes: string[];
	cityNames: string[];
	beneficiaries: { direct: number; indirect: number };
	beneficiariesDescription: TranslatableText;
	beneficiaryTypes: string[];
	impactEquivalent: TranslatableText;
	category: Category;
	subcategories: string[];
	sdgs: number[];
	fundingType: 'myPortal' | 'worldcoo' | 'both' | undefined;
	videoUrl: string;
	projectBudget: number;
	claim: TranslatableText;
	shortParagraph: TranslatableText;
	donationAmounts: { amount: number; equivalent: TranslatableText }[];
	moreInfo: TranslatableText;
	economicGoal: number;
	startDate: string;
	endDate: string;
	coverImage: File | undefined;
	coverImageAlt: TranslatableText;
	coverVideo: File | undefined;
	coverVideoAlt: TranslatableText;
	otherImages: File[];
	otherImagesAlt: TranslatableText[];
};

const defaultFormData: ProjectFormData = {
	id: '',
	internalName: '',
	name: translatable(),
	description: translatable(),
	justification: translatable(),
	objectives: translatable(),
	activities: translatable(),
	languages: ['SPA'],
	defaultLanguage: 'SPA',
	countryCodes: ['ES'],
	cityNames: [],
	beneficiaries: { direct: 0, indirect: 0 },
	beneficiariesDescription: translatable(),
	beneficiaryTypes: [],
	impactEquivalent: translatable(),
	category: 0,
	subcategories: [],
	sdgs: [],
	fundingType: undefined,
	videoUrl: '',
	projectBudget: 0,
	claim: translatable(),
	shortParagraph: translatable(),
	donationAmounts: DEFAULT_DONATION_AMOUNTS.map((amount) => ({ amount, equivalent: translatable() })),
	moreInfo: translatable(),
	economicGoal: 0,
	startDate: '',
	endDate: '',
	coverImage: undefined,
	coverImageAlt: translatable(),
	coverVideo: undefined,
	coverVideoAlt: translatable(),
	otherImages: [],
	otherImagesAlt: [],
};

export const TOTAL_STEPS = 5;
export const TOTAL_STEPS_FROM_PORTAL = 4;

export const useProjectFormStore = create<{
	_hasHydrated: boolean;
	setHasHydrated: (state: boolean) => void;
	currentStep: number;
	formData: ProjectFormData;
	setFormData: (partial: Partial<ProjectFormData>) => void;
	setCurrentStep: (step: number) => void;
	reset: () => void;
}>()(
	persist(
		(set) => ({
			_hasHydrated: false,
			setHasHydrated: (state) => set({ _hasHydrated: state }),
			currentStep: 1,
			formData: { ...defaultFormData, id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '' },
			setFormData: (partial) => set((state) => ({ formData: { ...state.formData, ...partial } })),
			setCurrentStep: (step) => set({ currentStep: step }),
			reset: () =>
				set({
					currentStep: 1,
					formData: { ...defaultFormData, id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '' },
				}),
		}),
		{
			name: 'project-form-storage',
			onRehydrateStorage: (state) => {
				return () => state.setHasHydrated(true);
			},
			// Files can't be serialized to localStorage, exclude them
			partialize: (state) => ({
				currentStep: state.currentStep,
				formData: {
					...state.formData,
					coverImage: undefined,
					coverVideo: undefined,
					otherImages: [],
					coverImageAlt: state.formData.coverImageAlt,
					coverVideoAlt: state.formData.coverVideoAlt,
					otherImagesAlt: state.formData.otherImagesAlt,
				},
			}),
			// Deep-merge formData so new default fields aren't lost on rehydration
			merge: (persisted, current) => {
				const p = persisted as typeof current;
				return {
					...current,
					...p,
					formData: { ...current.formData, ...p.formData },
				};
			},
		},
	),
);
